import { useCallback } from "react";
import { useFetcher, useMutator } from "~/hooks/useFetcher";
import {
  userPreferencesPath,
  type UserPreferencesResponse,
  type UserPreferencesUpdate,
} from "~/services/preferences";

export interface MemoriesPreference {
  /** Whether agents may read and write this user's memories. `null` until the
   *  preference has loaded, so the toggle can hold rather than flash a default
   *  the user never chose. */
  enabled: boolean | null;
  /** True while a change is being persisted. */
  isSaving: boolean;
  /** Records an explicit choice. Rejects are surfaced to the caller. */
  setEnabled: (next: boolean) => Promise<void>;
}

/**
 * Reads and writes the user's memories preference on the conversations
 * preferences endpoint. The switch moves optimistically and rolls back if the
 * write fails, so a failed save never leaves the UI claiming a setting the
 * service does not have.
 */
export function useMemoriesPreference(): MemoriesPreference {
  const key = userPreferencesPath();
  const { data } = useFetcher<UserPreferencesResponse>(key);
  const { trigger, isMutating } = useMutator<UserPreferencesResponse, UserPreferencesUpdate>(key, {
    requestInit: { method: "PUT" },
  });

  const setEnabled = useCallback(
    async (next: boolean) => {
      await trigger(
        { memories_enabled: next },
        {
          optimisticData: (current?: UserPreferencesResponse) =>
            ({ ...current, memories_enabled: next }) as UserPreferencesResponse,
          rollbackOnError: true,
        }
      );
    },
    [trigger]
  );

  return { enabled: data ? data.memories_enabled : null, isSaving: isMutating, setEnabled };
}
